import { type Table } from "@tanstack/react-table";
import cx from "classnames";
import {
	type ChangeEvent,
	type ForwardRefRenderFunction,
	type KeyboardEvent,
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { CloseIcon, EditIcon, GroupResourceIcon, SortAscendingIcon } from "@moment/icons";

import { Button } from "../../Button";
import { DropdownMenu } from "../../DropdownMenu";
import { InputSearchToggle } from "../../InputSearchToggle";
import { BasicTooltip } from "../../Tooltip";

export interface DataGridHeaderProps {
	table: Table<unknown>;
	titleValue?: string;
	onTitleValueChange?: (value: string) => void;
	searchQuery?: string;
	onSearchQueryChange: (query: string) => void;
}

const useDataGridTitle = (titleValue?: string, onTitleValueChange?: (value: string) => void) => {
	const isControlled = titleValue !== undefined;
	const [internalTitleValue, setInternalTitleValue] = useState(titleValue);
	const [isEditing, setIsEditing] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	const onFocus = useCallback(() => {
		setIsEditing(true);
	}, []);
	const onBlur = useCallback(() => {
		setIsEditing(false);

		onTitleValueChange?.(internalTitleValue ?? "");
		if (isControlled) {
			setInternalTitleValue(titleValue ?? "");
		}
	}, [internalTitleValue, isControlled, onTitleValueChange, titleValue]);
	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setInternalTitleValue(e.target.value);
	}, []);

	const onKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				inputRef.current?.blur();
				onBlur();
			} else if (e.key === "Escape") {
				setIsEditing(false);
				setInternalTitleValue(titleValue ?? "");
				inputRef.current?.blur();
			}
		},
		[onBlur, titleValue]
	);

	useEffect(() => {
		if (isEditing) {
			return;
		}

		setInternalTitleValue(titleValue);

		// Specifically ignoring `isEditing` in deps, we do not want that to replace contents.
		//
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [titleValue]);

	return { inputRef, isEditing, internalTitleValue, onFocus, onBlur, onChange, onKeyDown };
};

export const BaseDataGridHeader: ForwardRefRenderFunction<HTMLDivElement, DataGridHeaderProps> = (
	{ table, titleValue, onTitleValueChange, searchQuery, onSearchQueryChange },
	ref
) => {
	const isEditable: boolean = table.options.meta?.tableEditable || false;

	const columns = table.getAllLeafColumns();
	const [groupableColumns, groupedColumn, sortableColumns, sortedColumn] = useMemo(() => {
		const groupableColumns = columns.filter((column) => column.getCanGroup());
		const groupedColumn = groupableColumns.find((column) => column.getIsGrouped());
		const sortableColumns = columns.filter((column) => column.getCanSort());
		const sortedColumn = sortableColumns.find((column) => column.getIsSorted());
		return [groupableColumns, groupedColumn, sortableColumns, sortedColumn] as const;
	}, [columns]);

	const { inputRef, isEditing, internalTitleValue, onFocus, onBlur, onChange, onKeyDown } =
		useDataGridTitle(titleValue, onTitleValueChange);

	return (
		<div
			ref={ref}
			className={cx(
				"flex flex-col data-grid-header rounded-tl-md border border-transparent text-primary -mb-2 group-[.compute-cell]:absolute"
			)}
		>
			<div className="flex h-12 min-w-full rounded-tl-md shrink-0 flex-row items-center group-[.compute-cell]:focused:bg-brand-translucent-hover group-[.prosemirror-compute-cell--selected]:bg-brand-translucent-hover">
				<div className="group flex items-center">
					<input
						ref={inputRef}
						type="text"
						className={cx(
							`p-1 -ml-1 border-b-2 border-transparent focus:border-brand outline-none bg-transparent text-md font-bold`,
							{
								"w-fit": isEditing,
								"pr-6": !isEditing,
								"w-[6rem]": internalTitleValue?.length === 0,
							}
						)}
						placeholder="Untitled"
						size={internalTitleValue?.length ?? 6}
						value={internalTitleValue}
						onChange={onChange}
						onFocus={onFocus}
						onBlur={onBlur}
						onKeyDown={onKeyDown}
						disabled={!isEditable}
					/>
					{!isEditing && isEditable && (
						<EditIcon className="-ml-4 pointer-events-none fill-icon-default invisible group-hover:visible" />
					)}
				</div>

				<DropdownMenu.Root>
					<DropdownMenu.Trigger asChild>
						<div className="relative ml-auto">
							<BasicTooltip
								content={!isEditable && "Table is not editable"}
								triggerProps={{ asChild: true }}
							>
								<Button
									toggled={sortedColumn !== undefined}
									level="tertiary"
									size="sm"
									label="Sort by"
									className="mr-2"
									icon={SortAscendingIcon}
									disabled={!isEditable}
								/>
							</BasicTooltip>
							{sortedColumn !== undefined && (
								<div className="rounded-full bg-palette-brand-9 w-4 h-4 text-xxs text-fixed-light-default absolute bottom-0 right-0 justify-center items-center flex font-bold">
									1
								</div>
							)}
						</div>
					</DropdownMenu.Trigger>
					<DropdownMenu.Portal>
						<DropdownMenu.Content>
							<div className="px-4 py-2 text-sm text-secondary font-semibold">
								Select column to sort by
							</div>
							<DropdownMenu.RadioGroup value={sortedColumn?.id}>
								{sortableColumns.length === 0 && (
									<div className="px-4 py-2 text-sm text-secondary italic">
										No sortable columns
									</div>
								)}
								{sortableColumns.map((column) => {
									const direction = column.getNextSortingOrder();
									return (
										<DropdownMenu.RadioItem
											key={column.id}
											value={column.id}
											onSelect={() => {
												// We only allow one sorting at a time, so always
												// reset any other sorting before toggling this
												// one.
												table.resetSorting();
												column.toggleSorting();
											}}
											className="group/sort-item"
										>
											<div className="mr-8">{column.id}</div>
											<div className="ml-auto italic invisible group-data-[highlighted]/sort-item:visible">
												{direction === false
													? "unsort"
													: `sort ${direction}`}
											</div>
										</DropdownMenu.RadioItem>
									);
								})}
							</DropdownMenu.RadioGroup>
							<DropdownMenu.Separator />
							<DropdownMenu.Item
								variant="negative"
								onSelect={() => table.resetSorting()}
								disabled={sortedColumn === undefined}
							>
								<CloseIcon className="mr-2" />
								Clear sorting
							</DropdownMenu.Item>
						</DropdownMenu.Content>
					</DropdownMenu.Portal>
				</DropdownMenu.Root>

				<DropdownMenu.Root>
					<DropdownMenu.Trigger asChild>
						<div className="relative">
							<BasicTooltip
								content={!isEditable && "Table is not editable"}
								triggerProps={{ asChild: true }}
							>
								<Button
									toggled={groupedColumn !== undefined}
									level="tertiary"
									size="sm"
									className="mr-2"
									label="Group by"
									icon={GroupResourceIcon}
									disabled={!isEditable}
								/>
							</BasicTooltip>
							{groupedColumn !== undefined && (
								<div className="rounded-full bg-palette-brand-9 w-4 h-4 text-xxs text-fixed-light-default absolute bottom-0 right-0 justify-center items-center flex font-bold">
									1
								</div>
							)}
						</div>
					</DropdownMenu.Trigger>
					<DropdownMenu.Portal>
						<DropdownMenu.Content>
							<div className="px-4 py-2 text-sm text-secondary font-semibold">
								Select a column to group by
							</div>
							<DropdownMenu.RadioGroup value={groupedColumn?.id}>
								{groupableColumns.length === 0 && (
									<div className="px-4 py-2 text-sm text-secondary italic">
										No groupable columns
									</div>
								)}
								{groupableColumns.map((column) => {
									return (
										<DropdownMenu.RadioItem
											key={column.id}
											value={column.id}
											onSelect={() => column.toggleGrouping()}
										>
											{column.id}
										</DropdownMenu.RadioItem>
									);
								})}
							</DropdownMenu.RadioGroup>
							<DropdownMenu.Separator />
							<DropdownMenu.Item
								variant="negative"
								onSelect={() => table.resetGrouping()}
								disabled={groupedColumn === undefined}
							>
								<CloseIcon className="mr-2" />
								Clear grouping
							</DropdownMenu.Item>
						</DropdownMenu.Content>
					</DropdownMenu.Portal>
				</DropdownMenu.Root>

				<div className="flex shrink-0 space-x-2">
					<InputSearchToggle
						label="Search all columns (visible only to you)"
						defaultSearchQueryValue={searchQuery}
						onSearchQueryChange={onSearchQueryChange}
						disabled={!isEditable}
					/>
				</div>
			</div>
		</div>
	);
};

export const DataGridHeader = forwardRef(BaseDataGridHeader);
